import { DateTime } from '@mode-switch/express';
import { ColorTarget, Encoding, VariableType } from '@mode-vegas/vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { ChartDomain, SwitchViewDomain } from '@mode/shared/contract-common';
import { createAction, props } from '@ngrx/store';

export const selectChart = createAction('[Charts] SelectChart', props<{ token: string }>());
export const loadChartsFailure = createAction('[Charts] LoadChartsFailure', props<{ error: Error }>());
export const loadChartsSuccess = createAction(
  '[Charts] LoadChartsSuccess',
  props<{ queryToken: string; payload: ReadonlyArray<ChartTypes.ChartResource> }>()
);
export const convertLoadedCharts = createAction(
  '[Charts] ConvertLoadedCharts',
  props<{ queryToken: string; payload: ReadonlyArray<ChartTypes.ChartResource> }>()
);
export const loadReportViewChartSuccess = createAction(
  '[Report View Charts] Load Chart Success',
  props<{ queryToken: string; payload: ReadonlyArray<ChartTypes.ChartResource> }>()
);

export const updateEncoding = createAction(
  '[Charts] UpdateEncoding',
  props<{
    token: string;
    encoding: Encoding;
    format?: Partial<ChartTypes.ChartFormat>;
    facetSizes?: Readonly<ChartTypes.FacetSizeMap>;
    fieldFormats?: Readonly<ChartTypes.FieldFormatMap>;
    fieldColors?: Readonly<ChartTypes.FieldColorMap>;
    colorPaletteToken?: string;
  }>()
);

export const updateTitle = createAction('[Charts] UpdateTitle', props<{ token: string; title: string }>());
export const updateDescription = createAction(
  '[Charts] UpdateDescription',
  props<{ token: string; description: string }>()
);
export const updateCaption = createAction(
  '[Charts] UpdateCaption',
  props<{ token: string; caption: ChartTypes.CaptionInfo }>()
);
export const updateDetails = createAction(
  '[Charts] UpdateDetails',
  props<{ token: string; title: string; description: string }>()
);
export const updateType = createAction(
  '[Charts] UpdateType',
  props<{ token: string; chartType: ChartTypes.ChartType }>()
);
export const updateIsAutoUpdateDisabled = createAction(
  '[Charts] UpdateIsAutoUpdateDisabled',
  props<{ token: string; isAutoUpdateDisabled: boolean }>()
);
export const manuallyApplyChanges = createAction('[Charts] ManuallyApplyChanges', props<{ token: string }>());

export const setUnappliedChangesCount = createAction(
  '[Charts] SetUnappliedChangesCount',
  props<{ token: string; count: number }>()
);

export const swapAxes = createAction('[Charts] SwapAxes', props<{ token: string }>());
export const drilldown = createAction('[Charts] Drilldown', props<{ token: string; target: ChartTypes.MarkTarget }>());
export const changeHierarchy = createAction('[Charts] ChangeHierarchy', props<{ token: string; forward: boolean }>());
export const dynamicDrillDown = createAction(
  '[Charts] DynamicDrillDown',
  props<{
    token: string;
    target: ChartTypes.MarkTarget;
    targetFieldId: string;
    drillToField: string;
    dateTruncUnit?: DateTime.Unit;
  }>()
);
export const quickSort = createAction('[Charts] QuickSort', props<{ token: string; descending: boolean }>());
export const clearChart = createAction('[Charts] ClearChart', props<{ token: string }>());
export const clearFilters = createAction('[Charts] ClearFilters', props<{ token: string }>());
export const clearSorts = createAction('[Charts] ClearSorts', props<{ token: string }>());
export const clearFormatting = createAction('[Charts] ClearFormatting', props<{ token: string }>());
export const removeSort = createAction('[Charts] RemoveSort', props<{ token: string; formula: string }>());
export const updateFormat = createAction(
  '[Charts] UpdateFormat',
  props<{ token: string; format: ChartTypes.ChartFormat }>()
);
export const updateColors = createAction(
  '[Charts] UpdateColors',
  props<{
    token: string;
    colorPaletteToken: string;
    palette: string[];
    showAlert: boolean;
    colorMap: {
      [seriesName: string]: string;
    };
  }>()
);
export const updateDesignerColors = createAction(
  '[Charts] UpdateDesignerColors',
  props<{
    token: string;
    colorPaletteToken: string;
    palette: string[];
    showAlert: boolean;
    colorMap: {
      [seriesName: string]: string;
    };
  }>()
);
export const updateExplorationColors = createAction(
  '[Charts] UpdateExplorationColors',
  props<{
    token: string;
    colorPaletteToken: string;
    palette: string[];
    showAlert: boolean;
    colorMap: {
      [seriesName: string]: string;
    };
  }>()
);
export const updateFieldColor = createAction(
  '[Charts] UpdateFieldColor',
  props<{
    token: string;
    formula: string;
    varType: VariableType;
    colorPaletteToken: string;
    palette: string[];
    series?: string;
    colorMap: ChartTypes.ColorEntries;
    gradient?: ChartTypes.ContinuousGradient;
    unassignedColor?: string;
    colorTarget?: ColorTarget;
  }>()
);
export const updateDesignerFieldColor = createAction(
  '[Charts] UpdateDesignerFieldColor',
  props<{
    token: string;
    formula: string;
    varType: VariableType;
    colorPaletteToken: string;
    palette: string[];
    series?: string;
    colorMap: ChartTypes.ColorEntries;
    gradient?: ChartTypes.ContinuousGradient;
    unassignedColor?: string;
    colorTarget?: ColorTarget;
  }>()
);
export const updateExplorationFieldColor = createAction(
  '[Charts] UpdateExplorationFieldColor',
  props<{
    token: string;
    formula: string;
    varType: VariableType;
    colorPaletteToken: string;
    palette: string[];
    series?: string;
    colorMap: ChartTypes.ColorEntries;
    gradient?: ChartTypes.ContinuousGradient;
    unassignedColor?: string;
    colorTarget?: ColorTarget;
  }>()
);

export const updateFacetSizes = createAction(
  '[Charts] updateFacetSizes',
  props<{
    facetSizes: ChartTypes.FacetSizeMap;
    token: string;
  }>()
);

export const updateEncodingAndFormat = createAction(
  '[Charts] updateEncodingAndFormat',
  props<{
    token: string;
    encoding: Encoding;
    facetSizes?: Readonly<ChartTypes.FacetSizeMap>;
    fieldFormats?: Readonly<ChartTypes.FieldFormatMap>;
    format?: Partial<ChartTypes.ChartFormat>;
    fieldColors?: Readonly<ChartTypes.FieldColorMap>;
    colorPaletteToken?: string;
  }>()
);

export const replaceChartHistory = createAction(
  '[Charts] replaceChartHistory',
  props<{
    token: string;
    encoding?: Encoding;
    isAutoUpdateDisabled?: boolean;
  }>()
);

export const updateFieldFormat = createAction(
  '[Charts] UpdateFieldFormat',
  props<{ token: string; formula: string; pane: ChartTypes.FieldPaneTypes; format: ChartTypes.TextFormat }>()
);

export const updateAxisFieldFormat = createAction(
  '[Charts] UpdateAxisFieldFormat',
  props<{
    format: ChartTypes.AxisFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateBaseFormatDisplayName = createAction(
  '[Chart] UpdateBaseFormatDisplayName',
  props<{
    format: ChartTypes.BaseFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateHeaderFieldFormat = createAction(
  '[Charts] UpdateHeaderFieldFormat',
  props<{
    format: ChartTypes.HeaderFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateLegendFieldFormat = createAction(
  '[Charts] UpdateLegendFieldFormat',
  props<{
    format: ChartTypes.LegendFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updatePaneFieldFormat = createAction(
  '[Charts] UpdatePaneFieldFormat',
  props<{
    format: ChartTypes.PaneFieldFormat;
    varType: VariableType;
    formula: string;
    token: string;
  }>()
);

export const updateBigValueDescription = createAction(
  '[Charts] UpdateBigValueDescription',
  props<{ token: string; description: string }>()
);

export const undo = createAction('[Charts] Undo', props<{ token: string; snapshot: ChartTypes.ChartSnapshot }>());
export const undoTemp = createAction(
  '[Charts] UndoTemp',
  props<{ token: string; snapshot: ChartTypes.ChartSnapshot }>()
);
export const redo = createAction('[Charts] Redo', props<{ token: string; snapshot: ChartTypes.ChartSnapshot }>());
export const redoTemp = createAction(
  '[Charts] RedoTemp',
  props<{ token: string; snapshot: ChartTypes.ChartSnapshot }>()
);

export const setOffset = createAction(
  '[Charts] SetOffset',
  props<{
    token: string;
    offset: number;
  }>()
);

export const resetOffset = createAction(
  '[Charts] ResetOffset',
  props<{
    token: string;
  }>()
);

export const setPageSize = createAction(
  '[Charts] SetPageSize',
  props<{
    token: string;
    pageSize: number;
  }>()
);

export const setTableColumnSearch = createAction(
  '[Charts] SetTableColumnSearch',
  props<{
    token: string;
    columnSearchModel: ChartTypes.TableColumnSearch;
  }>()
);

export const updateChart = createAction(
  '[Charts] UpdateChart',
  props<{ token: string; chart: ChartTypes.ChartSnapshot; description: string }>()
);
export const updateChartTemp = createAction(
  '[Charts] UpdateChartTemp',
  props<{ token: string; chart: ChartTypes.ChartSnapshot; description: string; noUndo?: boolean }>()
);
export const updateChartNoEncoding = createAction(
  '[Charts] updateChartNoEncoding',
  props<{ token: string; chart: ChartTypes.ChartSnapshot; description: string }>()
);
export const updateChartNoEncodingTemp = createAction(
  '[Charts] updateChartNoEncodingTemp',
  props<{ token: string; chart: ChartTypes.ChartSnapshot; description: string; noUndo?: boolean }>()
);
export const updateChartSuccess = createAction(
  '[Charts] UpdateChartSuccess',
  props<{ resource: ChartTypes.ChartResource }>()
);
export const updateChartFailure = createAction('[Charts] UpdateChartFailure', props<{ token: string; error: Error }>());

export const backfillTableFormats = createAction(
  '[Charts] BackfillTableFormats',
  props<{
    filledFieldFormatMaps: { token: string; filledFieldFormats: ChartTypes.FieldFormatMap }[];
    filledFormats: { token: string; filledFormat: ChartTypes.ChartFormat }[];
  }>()
);

export const createChart = createAction(
  '[Charts] CreateChart',
  props<{
    queryToken: string;
    chartType?: ChartTypes.ChartType | ChartTypes.V1ChartType;
    viewVersion?: ChartTypes.ChartResource['view_version'];
  }>()
);

export const createChartSuccess = createAction(
  '[Charts] CreateChartSuccess',
  props<{
    payload: ChartTypes.ChartResource;
    queryToken: string;
    position?: number;
    focus?: boolean;
  }>()
);

export const createChartFailure = createAction(
  '[Charts] CreateChartFailure',
  props<{
    error: Error;
  }>()
);

export const destroyChart = createAction(
  '[Charts] DestroyChart',
  props<{
    payload: string;
  }>()
);
export const destroyChartFailure = createAction(
  '[Charts] DestroyChartFailure',
  props<{
    error: Error;
    token: string;
    queryToken: string;
  }>()
);

export const destroyChartSuccess = createAction(
  '[Charts] DestroyChartSuccess',
  props<{
    payload: string;
  }>()
);

export const cloneChart = createAction(
  '[Charts] CloneChart',
  props<{ token: string; holdView?: boolean; position?: number }>()
);
export const openExploreModal = createAction('[Charts] OpenExploreModal', props<{ token: string }>());

export const makeCopy = createAction(
  '[Charts] MakeCopy',
  props<{ token: string; originalToken: string; chart?: Partial<ChartTypes.ChartSnapshot> }>()
);
export const makeCopyTemp = createAction(
  '[Charts] MakeCopyTemp',
  props<{ token: string; originalToken: string; chart?: Partial<ChartTypes.ChartSnapshot> }>()
);

export const loadGfVisualization = createAction(
  '[Charts] LoadGfVisualization',
  props<{ visualization: ChartTypes.ChartResource; dataView: SwitchViewDomain.DataView }>()
);

export const loadChartsFromSwitchViewSuccess = createAction(
  '[Charts] LoadChartsFromSwitchViewSuccess',
  props<{ charts: ChartDomain.ChartWithQueryToken[] }>()
);

export const reportViewerChartRendered = createAction('[Report Viewer Component] Chart Rendered');

export const viewUnaggregatedChartData = createAction(
  '[Report Viewer Component] View Unaggregated Chart Data',
  props<{ chartToken: string }>()
);

// GF TODO: implement this once we can fetch all from api
// export const loadGfVisualizations = createAction(
//   '[Charts] LoadGfVisualization',
//   props<{ visualization: any; switchView: any; runResultMetadata: any, switchDataset: any; }>()
// );

export type Union =
  | ReturnType<typeof selectChart>
  | ReturnType<typeof loadChartsFailure>
  | ReturnType<typeof loadChartsSuccess>
  | ReturnType<typeof loadChartsFromSwitchViewSuccess>
  | ReturnType<typeof loadReportViewChartSuccess>
  | ReturnType<typeof convertLoadedCharts>
  | ReturnType<typeof updateChart>
  | ReturnType<typeof updateChartNoEncoding>
  | ReturnType<typeof updateChartSuccess>
  | ReturnType<typeof updateChartTemp>
  | ReturnType<typeof updateChartNoEncodingTemp>
  | ReturnType<typeof updateChartFailure>
  | ReturnType<typeof backfillTableFormats>
  | ReturnType<typeof createChart>
  | ReturnType<typeof createChartFailure>
  | ReturnType<typeof createChartSuccess>
  | ReturnType<typeof destroyChart>
  | ReturnType<typeof destroyChartFailure>
  | ReturnType<typeof destroyChartSuccess>
  | ReturnType<typeof cloneChart>
  | ReturnType<typeof updateEncoding>
  | ReturnType<typeof updateTitle>
  | ReturnType<typeof updateDescription>
  | ReturnType<typeof updateCaption>
  | ReturnType<typeof updateDetails>
  | ReturnType<typeof updateType>
  | ReturnType<typeof updateIsAutoUpdateDisabled>
  | ReturnType<typeof manuallyApplyChanges>
  | ReturnType<typeof setUnappliedChangesCount>
  | ReturnType<typeof swapAxes>
  | ReturnType<typeof drilldown>
  | ReturnType<typeof changeHierarchy>
  | ReturnType<typeof dynamicDrillDown>
  | ReturnType<typeof quickSort>
  | ReturnType<typeof clearChart>
  | ReturnType<typeof clearFilters>
  | ReturnType<typeof clearSorts>
  | ReturnType<typeof clearFormatting>
  | ReturnType<typeof removeSort>
  | ReturnType<typeof updateFormat>
  | ReturnType<typeof updateColors>
  | ReturnType<typeof updateDesignerColors>
  | ReturnType<typeof updateExplorationColors>
  | ReturnType<typeof updateFieldColor>
  | ReturnType<typeof updateDesignerFieldColor>
  | ReturnType<typeof updateExplorationFieldColor>
  | ReturnType<typeof updateFieldFormat>
  | ReturnType<typeof updateAxisFieldFormat>
  | ReturnType<typeof updateBaseFormatDisplayName>
  | ReturnType<typeof updateHeaderFieldFormat>
  | ReturnType<typeof updateLegendFieldFormat>
  | ReturnType<typeof updateFacetSizes>
  | ReturnType<typeof updateEncodingAndFormat>
  | ReturnType<typeof replaceChartHistory>
  | ReturnType<typeof updatePaneFieldFormat>
  | ReturnType<typeof updateBigValueDescription>
  | ReturnType<typeof setOffset>
  | ReturnType<typeof resetOffset>
  | ReturnType<typeof setPageSize>
  | ReturnType<typeof setTableColumnSearch>
  | ReturnType<typeof undo>
  | ReturnType<typeof undoTemp>
  | ReturnType<typeof redo>
  | ReturnType<typeof redoTemp>
  | ReturnType<typeof makeCopy>
  | ReturnType<typeof makeCopyTemp>
  | ReturnType<typeof loadGfVisualization>
  | ReturnType<typeof reportViewerChartRendered>
  | ReturnType<typeof viewUnaggregatedChartData>;
